import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
  CONTRACTOR_INVOICE_STATUSES,
  INVOICE_SORT_FIELDS,
  INVOICE_VALIDATION_STATUSES,
  SORT_DIRECTIONS,
  isValidYearMonth,
  type InvoiceSortField,
} from "@contractorhub/shared";
import { InvoiceInbox } from "../../../components/invoices/invoice-inbox";
import { useRememberList } from "../../../lib/list-return";
import {
  DATE_SORTS,
  dateSortOf,
  type InboxTab,
} from "../../../components/invoices/invoice-filter-bar";
import {
  adminContractorsQuery,
  adminInvoicesQuery,
  adminProjectsQuery,
} from "../../../lib/queries";

// A reversed month range (a hand-typed or old link) is swapped rather than sent:
// the API answers it with a 400, and a 400 in the loader is the error page.
const searchSchema = z
  .object({
    status: z.enum([...CONTRACTOR_INVOICE_STATUSES, "ALL"]).default("SUBMITTED"),
    validationStatus: z.enum(INVOICE_VALIDATION_STATUSES).optional(),
    q: z.string().max(120).optional(),
    contractorId: z.string().optional(),
    projectId: z.string().optional(),
    untaggedOnly: z.enum(["true", "false"]).optional(),
    changedOnly: z.enum(["true", "false"]).optional(),
    from: z.string().refine(isValidYearMonth).optional(),
    to: z.string().refine(isValidYearMonth).optional(),
    sort: z.enum(INVOICE_SORT_FIELDS).default("submittedAt"),
    dir: z.enum(SORT_DIRECTIONS).default("desc"),
    page: z.coerce.number().int().min(1).default(1),
  })
  .transform((search) =>
    search.from && search.to && search.from > search.to
      ? { ...search, from: search.to, to: search.from }
      : search
  );

export const Route = createFileRoute("/_authed/_admin/invoices")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(adminInvoicesQuery(deps)),
      context.queryClient.ensureQueryData(adminContractorsQuery),
      context.queryClient.ensureQueryData(adminProjectsQuery),
    ]),
  component: AdminInvoicesPage,
});

function AdminInvoicesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(adminInvoicesQuery(search));
  useRememberList("invoices", search, data.rows.map((row) => row.id));
  const { data: contractorData } = useSuspenseQuery(adminContractorsQuery);
  const { data: projectData } = useSuspenseQuery(adminProjectsQuery);

  // Merge into the existing search and reset the page, as the queue does.
  const patch = (next: Partial<typeof search>) =>
    void navigate({ to: "/invoices", search: { ...search, ...next, page: 1 } });

  return (
    <InvoiceInbox
      tab={search.status}
      counts={data.counts}
      summary={data.summary}
      rows={data.rows}
      pageInfo={data.pageInfo}
      filters={search}
      contractors={contractorData.contractors.map((c) => ({ id: c.id, name: c.name }))}
      projects={projectData.projects.map((p) => ({ id: p.id, name: p.name, clientName: p.clientName }))}
      onFilterChange={patch}
      onSortChange={(sort: InvoiceSortField) =>
        patch({
          sort,
          dir: search.sort === sort && search.dir === "desc" ? "asc" : "desc",
        })
      }
      onClearFilters={() =>
        void navigate({
          to: "/invoices",
          search: { status: search.status, sort: dateSortOf(search.status), dir: "desc", page: 1 },
        })
      }
      onTabChange={(tab: InboxTab) =>
        // A tab's date column moves with it (CINV-34); any other sort stays.
        patch(
          DATE_SORTS.includes(search.sort)
            ? { status: tab, sort: dateSortOf(tab), dir: "desc" }
            : { status: tab }
        )
      }
      onPageChange={(next: number) =>
        void navigate({ to: "/invoices", search: { ...search, page: next } })
      }
    />
  );
}
